import {
  queryOptions,
  skipToken,
  useQueries,
  useQuery
} from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { type Avdeling, type Region, Seksjon, Team } from '../types/types'
import { OwnerId } from '../utils/taskUtils'
import { useCallback } from 'react'
import { throwOnError } from '../utils/throwOnError'

type AuthFetch = ReturnType<typeof useAuth>['authFetch']

const isNotFoundError = (error: unknown): boolean =>
  error != null &&
  typeof error === 'object' &&
  'status' in error &&
  error.status === 404

interface OwnerName {
  id: string
  name?: string
  type?: string
}

export const isOwnerName = (item: OwnerName | undefined): item is OwnerName => {
  return item != null
}

const fetchRegionName = async (
  authFetch: AuthFetch,
  ownerIdString: string,
  owner: string
): Promise<OwnerName> => {
  const { data, error } = await authFetch().GET(
    '/orgenhet-api/regioner/{regionid}',
    { params: { path: { regionid: owner } } }
  )
  if (error) throwOnError(error)
  return {
    id: ownerIdString,
    name: (data as unknown as Region).navn,
    type: 'Region'
  }
}

const fetchAvdelingName = async (
  authFetch: AuthFetch,
  ownerIdString: string,
  owner: string
): Promise<OwnerName> => {
  const { data, error } = await authFetch().GET(
    '/orgenhet-api/avdelinger/{avdelingid}',
    { params: { path: { avdelingid: owner } } }
  )
  if (error) throwOnError(error)
  return {
    id: ownerIdString,
    name: (data as unknown as Avdeling).navn,
    type: 'Avdeling'
  }
}

const fetchSeksjonName = async (
  authFetch: AuthFetch,
  ownerIdString: string,
  owner: string
): Promise<OwnerName> => {
  const { data, error } = await authFetch().GET(
    '/orgenhet-api/seksjoner/{seksjonid}',
    { params: { path: { seksjonid: owner } } }
  )
  if (error) throwOnError(error)
  return {
    id: ownerIdString,
    name: (data as unknown as Seksjon).navn,
    type: 'Seksjon'
  }
}

const fetchUserName = async (
  authFetch: AuthFetch,
  ownerIdString: string,
  owner: string
): Promise<OwnerName> => {
  const { data, error } = await authFetch().GET('/users/{userId}', {
    params: { path: { userId: owner } }
  })
  if (error) throwOnError(error)
  return { id: ownerIdString, name: data.name, type: 'Bruker' }
}

const fetchTeamName = async (
  authFetch: AuthFetch,
  ownerIdString: string,
  owner: string
): Promise<OwnerName> => {
  try {
    const { data, error } = await authFetch().GET(
      '/orgenhet-api/team/{teamid}',
      { params: { path: { teamid: Number(owner) } } }
    )
    if (isNotFoundError(error)) {
      return { id: ownerIdString, name: ownerIdString, type: 'Team' }
    }
    if (error) throwOnError(error)
    return {
      id: ownerIdString,
      name: (data as unknown as Team).name,
      type: 'Team'
    }
  } catch (error) {
    if (isNotFoundError(error)) {
      return { id: ownerIdString, name: ownerIdString, type: 'Team' }
    }
    throw error
  }
}

const fetchOwnerName = async (
  authFetch: AuthFetch,
  ownerIdString: string
): Promise<OwnerName> => {
  const ownerId = OwnerId.fromString(ownerIdString)
  if (ownerId == null) return { id: ownerIdString, name: ownerIdString }

  const owner = ownerId.owner

  switch (ownerId.type) {
    case 'region':
      return fetchRegionName(authFetch, ownerIdString, owner)
    case 'avdeling':
      return fetchAvdelingName(authFetch, ownerIdString, owner)
    case 'seksjon':
      return fetchSeksjonName(authFetch, ownerIdString, owner)
    case 'user':
      return fetchUserName(authFetch, ownerIdString, owner)
    case 'team':
      return fetchTeamName(authFetch, ownerIdString, owner)
  }

  return { id: ownerIdString, name: ownerIdString }
}

const getOwnerNameOptions = (authFetch: AuthFetch, ownerIdString?: string) => {
  return queryOptions({
    staleTime: 1000 * 60 * 60 * 24 * 7, // 1 week
    queryKey: ['ownerIdString', ownerIdString],
    queryFn:
      ownerIdString != null && ownerIdString.length > 0
        ? async () => fetchOwnerName(authFetch, ownerIdString)
        : skipToken
  })
}

export const useGetOwnerNameQueries = (ownerIds: Array<string | undefined>) => {
  const { authFetch } = useAuth()
  const uniqueOwnerIds = Array.from(
    new Set(
      (ownerIds ?? [])
        .filter((id): id is string => typeof id === 'string')
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
    )
  )

  return useQueries({
    queries: uniqueOwnerIds.map((id) => getOwnerNameOptions(authFetch, id)),
    combine: (results) => {
      return {
        data: results.map((result) => result.data).filter(isOwnerName),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError)
      }
    }
  })
}

export const useGetOwnerNameQuery = (ownerIdString?: string) => {
  const { authFetch } = useAuth()
  return useQuery(getOwnerNameOptions(authFetch, ownerIdString))
}

export const useGetOwnerNameQueriesDynamic = (
  ownerIds: Array<string | undefined>
) => {
  const { authFetch } = useAuth()

  const getOptions = useCallback(
    (ownerIdString?: string) => getOwnerNameOptions(authFetch, ownerIdString),
    [authFetch]
  )

  return useQueries({
    queries: ownerIds.map(getOptions),
    combine: (results) => ({
      data: results.map((r) => r.data).filter(isOwnerName),
      isLoading: results.some((r) => r.isLoading),
      isError: results.some((r) => r.isError)
    })
  })
}
